import ColorTile from 'Components/smallComponents/colorTile';
import type { TemplateResult } from 'Components/types';
import { dialogOpened } from 'Core/actions/dialog';
import { toggleColor } from 'Core/actions/request';
import { searchResponseStateSelector } from 'Core/selectors/search';
import { useDispatch, useSelector } from 'Core/store';
import { FacetValue, WeightedColor } from 'Models/index';
import { cloneSafe } from 'Utils/components';
import { simpleHandler } from 'Utils/roleHandler';

import type { FacetCommonProps } from '../baseFacet';
import { ColorSlider } from '../common/index';

const defaultPaletteColors: Array<[string, string]> = [
  ['Black', '101010'],
  ['Taupe', 'b09c82'],
  ['Gray', '808080'],
  ['Silver', 'c0c0c0'],
  ['Beige', 'f5f5dc'],
  ['White', 'ffffff'],
  ['Burgundy', '750f17'],
  ['Red', 'c00718'],
  ['Terracotta', 'b24d38'],
  ['Coral', 'f77564'],
  ['Orange', 'fa760a'],
  ['Peach', 'fbbd93'],
  ['Brown', '4f2907'],
  ['Dark Green', '204b21'],
  ['Olive', '808000'],
  ['Green', '248a0f'],
  ['Yellow', 'ffd246'],
  ['Gold', 'deaa0d'],
  ['Navy', '132b53'],
  ['Blue', '1f5e9e'],
  ['Peacock', '008080'],
  ['Aqua', '55c2c3'],
  ['Light Blue', '90c1e4'],
  ['Purple', '67357e'],
  ['Pink', 'eb6fa4'],
  ['Tan', 'dac8a0'],
];

export default function AdvancedColorFacet({
  field,
  selection,
  config: { paletteColors = defaultPaletteColors },
  templateFunc,
  facetRef,
  commonParams,
  commonRoles,
}: FacetCommonProps): TemplateResult {
  const dispatch = useDispatch();

  const noResults = useSelector(searchResponseStateSelector) === 'nothing';
  if (noResults) {
    return null;
  }

  const sliders = selection.map((sel) => {
    const { color } = new WeightedColor(sel.term);
    const props = { color, field };
    return <ColorSlider {...props} key={color} />;
  });

  const paletteItems = paletteColors.map(([name, color]: [string, string]) => {
    const { term } = new WeightedColor(color);
    const value = { field, term };
    const isSelected = FacetValue.isInCollection(value, selection);
    const onClick = () => dispatch(toggleColor(value, { mayDiscardValue: true }));
    const props = { color, isSelected, name, onClick };
    return <ColorTile {...props} key={color} />;
  });

  const onClick = simpleHandler({ ...commonRoles });

  const openSimplePaletteDialog = () => dispatch(dialogOpened('SimpleColorPaletteDialog'));
  const openColorExtractorDialog = () => {
    uploadImage().then(
      (extractorData) => dispatch(dialogOpened('ColorExtractorDialog', { extractorData })),
      (err) => err && console.warn(err),
    );
  };

  const component = templateFunc.call({
    ...commonParams,
    sliders,
    paletteItems,
    openSimplePaletteDialog,
    openColorExtractorDialog,
  });
  return cloneSafe(component, facetRef, { onClick });
}

function uploadImage() {
  return new Promise<File>((res, rej) => {
    const input = document.getElementById(CONVERMAX_IMAGE_BUFFER_ID);
    if (!(input instanceof HTMLInputElement)) {
      rej('Image buffer input not found');
      return;
    }
    input.click();
    input.onchange = ({ target }) => {
      const file = target instanceof HTMLInputElement ? target.files?.[0] : undefined;
      input.value = '';
      if (file) {
        res(file);
      } else {
        rej('No file provided');
      }
    };
  }).then(
    (file: File) =>
      new Promise<{ filename: string; src: string }>((res, rej) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = ({ target }) => {
          const result = target instanceof FileReader ? target.result : null;
          if (typeof result === 'string') {
            res({ filename: file.name, src: result });
          } else {
            rej('ArrayBuffer support needed');
          }
        };
      }),
  );
}
